/* eslint-disable */
"use client";
import * as React from "react";
import {
  Button,
  Flex,
  Grid,
  SelectField,
  SwitchField,
  TextField,
} from "@aws-amplify/ui-react";
import { fetchByPath, getOverrideProps, validateField } from "./utils";
import { generateClient } from "aws-amplify/api";
import { getDanceStyle } from "./graphql/queries";
import { updateDanceStyle } from "./graphql/mutations";
const client = generateClient();
export default function DanceStyleUpdateForm(props) {
  const {
    id: idProp,
    danceStyle: danceStyleModelProp,
    onSuccess,
    onError,
    onSubmit,
    onValidate,
    onChange,
    overrides,
    ...rest
  } = props;
  const initialValues = {
    name: "",
    discipline: "",
    beatsPerBar: "",
    status: true,
  };
  const [name, setName] = React.useState(initialValues.name);
  const [discipline, setDiscipline] = React.useState(initialValues.discipline);
  const [beatsPerBar, setBeatsPerBar] = React.useState(
    initialValues.beatsPerBar
  );
  const [status, setStatus] = React.useState(initialValues.status);
  const [errors, setErrors] = React.useState({});
  const resetStateValues = () => {
    const cleanValues = danceStyleRecord
      ? { ...initialValues, ...danceStyleRecord }
      : initialValues;
    setName(cleanValues.name);
    setDiscipline(cleanValues.discipline);
    setBeatsPerBar(cleanValues.beatsPerBar);
    setStatus(cleanValues.status);
    setErrors({});
  };
  const [danceStyleRecord, setDanceStyleRecord] =
    React.useState(danceStyleModelProp);
  React.useEffect(() => {
    const queryData = async () => {
      const record = idProp
        ? (
            await client.graphql({
              query: getDanceStyle.replaceAll("__typename", ""),
              variables: { id: idProp },
            })
          )?.data?.getDanceStyle
        : danceStyleModelProp;
      setDanceStyleRecord(record);
    };
    queryData();
  }, [idProp, danceStyleModelProp]);
  React.useEffect(resetStateValues, [danceStyleRecord]);
  const validations = {
    name: [{ type: "Required" }],
    discipline: [],
    beatsPerBar: [],
    status: [],
  };
  const runValidationTasks = async (
    fieldName,
    currentValue,
    getDisplayValue
  ) => {
    const value =
      currentValue && getDisplayValue
        ? getDisplayValue(currentValue)
        : currentValue;
    let validationResponse = validateField(value, validations[fieldName]);
    const customValidator = fetchByPath(onValidate, fieldName);
    if (customValidator) {
      validationResponse = await customValidator(value, validationResponse);
    }
    setErrors((errors) => ({ ...errors, [fieldName]: validationResponse }));
    return validationResponse;
  };
  return (
    <Grid
      as="form"
      rowGap="15px"
      columnGap="15px"
      padding="20px"
      onSubmit={async (event) => {
        event.preventDefault();
        let modelFields = {
          name,
          discipline: discipline ?? null,
          beatsPerBar: beatsPerBar ?? null,
          status: status ?? null,
        };
        const validationResponses = await Promise.all(
          Object.keys(validations).reduce((promises, fieldName) => {
            if (Array.isArray(modelFields[fieldName])) {
              promises.push(
                ...modelFields[fieldName].map((item) =>
                  runValidationTasks(fieldName, item)
                )
              );
              return promises;
            }
            promises.push(
              runValidationTasks(fieldName, modelFields[fieldName])
            );
            return promises;
          }, [])
        );
        if (validationResponses.some((r) => r.hasError)) {
          return;
        }
        if (onSubmit) {
          modelFields = onSubmit(modelFields);
        }
        try {
          Object.entries(modelFields).forEach(([key, value]) => {
            if (typeof value === "string" && value === "") {
              modelFields[key] = null;
            }
          });
          await client.graphql({
            query: updateDanceStyle.replaceAll("__typename", ""),
            variables: {
              input: {
                id: danceStyleRecord.id,
                ...modelFields,
              },
            },
          });
          if (onSuccess) {
            onSuccess(modelFields);
          }
        } catch (err) {
          if (onError) {
            const messages = err.errors.map((e) => e.message).join("\n");
            onError(modelFields, messages);
          }
        }
      }}
      {...getOverrideProps(overrides, "DanceStyleUpdateForm")}
      {...rest}
    >
      <TextField
        label="Name"
        isRequired={true}
        isReadOnly={false}
        value={name}
        onChange={(e) => {
          let { value } = e.target;
          if (onChange) {
            const modelFields = {
              name: value,
              discipline,
              beatsPerBar,
              status,
            };
            const result = onChange(modelFields);
            value = result?.name ?? value;
          }
          if (errors.name?.hasError) {
            runValidationTasks("name", value);
          }
          setName(value);
        }}
        onBlur={() => runValidationTasks("name", name)}
        errorMessage={errors.name?.errorMessage}
        hasError={errors.name?.hasError}
        {...getOverrideProps(overrides, "name")}
      ></TextField>
      <SelectField
        label="Discipline"
        placeholder="Please select an option"
        isDisabled={false}
        value={discipline}
        onChange={(e) => {
          let { value } = e.target;
          if (onChange) {
            const modelFields = {
              name,
              discipline: value,
              beatsPerBar,
              status,
            };
            const result = onChange(modelFields);
            value = result?.discipline ?? value;
          }
          if (errors.discipline?.hasError) {
            runValidationTasks("discipline", value);
          }
          setDiscipline(value);
        }}
        onBlur={() => runValidationTasks("discipline", discipline)}
        errorMessage={errors.discipline?.errorMessage}
        hasError={errors.discipline?.hasError}
        {...getOverrideProps(overrides, "discipline")}
      >
        <option
          children="Standard"
          value="Standard"
          {...getOverrideProps(overrides, "disciplineoption0")}
        ></option>
        <option
          children="Latin"
          value="Latin"
          {...getOverrideProps(overrides, "disciplineoption1")}
        ></option>
      </SelectField>
      <TextField
        label="Beats per bar"
        isRequired={true}
        isReadOnly={false}
        type="number"
        step="any"
        value={beatsPerBar}
        onChange={(e) => {
          let value = isNaN(parseInt(e.target.value))
            ? e.target.value
            : parseInt(e.target.value);
          if (onChange) {
            const modelFields = {
              name,
              discipline,
              beatsPerBar: value,
              status,
            };
            const result = onChange(modelFields);
            value = result?.beatsPerBar ?? value;
          }
          if (errors.beatsPerBar?.hasError) {
            runValidationTasks("beatsPerBar", value);
          }
          setBeatsPerBar(value);
        }}
        onBlur={() => runValidationTasks("beatsPerBar", beatsPerBar)}
        errorMessage={errors.beatsPerBar?.errorMessage}
        hasError={errors.beatsPerBar?.hasError}
        {...getOverrideProps(overrides, "beatsPerBar")}
      ></TextField>
      <SwitchField
        label="Status"
        defaultChecked={false}
        isDisabled={false}
        isChecked={status}
        onChange={(e) => {
          let value = e.target.checked;
          if (onChange) {
            const modelFields = {
              name,
              discipline,
              beatsPerBar,
              status: value,
            };
            const result = onChange(modelFields);
            value = result?.status ?? value;
          }
          if (errors.status?.hasError) {
            runValidationTasks("status", value);
          }
          setStatus(value);
        }}
        onBlur={() => runValidationTasks("status", status)}
        errorMessage={errors.status?.errorMessage}
        hasError={errors.status?.hasError}
        {...getOverrideProps(overrides, "status")}
      ></SwitchField>
      <Flex
        justifyContent="space-between"
        {...getOverrideProps(overrides, "CTAFlex")}
      >
        <Button
          children="Reset"
          type="reset"
          onClick={(event) => {
            event.preventDefault();
            resetStateValues();
          }}
          isDisabled={!(idProp || danceStyleModelProp)}
          {...getOverrideProps(overrides, "ResetButton")}
        ></Button>
        <Flex
          gap="15px"
          {...getOverrideProps(overrides, "RightAlignCTASubFlex")}
        >
          <Button
            children="Submit"
            type="submit"
            variation="primary"
            isDisabled={
              !(idProp || danceStyleModelProp) ||
              Object.values(errors).some((e) => e?.hasError)
            }
            {...getOverrideProps(overrides, "SubmitButton")}
          ></Button>
        </Flex>
      </Flex>
    </Grid>
  );
}
